import { useStore } from "@tanstack/react-form";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { Building05 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Select } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import {
	createAppColumnHelper,
	FeaturedIconCell,
	PrimaryTextCell,
	SecondaryTextCell,
	TABLE_ICON_GUTTER_WIDTH_PX,
	TABLE_SELECTION_GUTTER_WIDTH_PX,
} from "@/components/Molecules/Table";
import { TableListEmptyState } from "@/components/Molecules/Table/TableListEmptyState";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { TableView } from "@/components/Molecules/Table/TableView";
import { useEditOrgUserSubOrgsForm } from "@/forms/(org-admin)/user/edit-org-user-sub-orgs/edit-org-user-sub-orgs.form.hooks";
import type {
	DataAccess,
	SubOrgFormRow,
} from "@/forms/(org-admin)/user/edit-org-user-sub-orgs/edit-org-user-sub-orgs.form.types";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import {
	type DataAccessDisplay,
	organizationQueryOptions,
	SUB_ORGANIZATION_MAX_PAGE_SIZE,
	type SubOrganizationDisplay,
} from "@/services/organization";
import { SearchSchema } from "../../$orgUserId";

// Combined type that merges SubOrganizationDisplay with DataAccessDisplay info
export type SubOrgWithDataAccess = SubOrganizationDisplay & {
	dataAccess: DataAccessDisplay | null;
	isAssigned: boolean;
};

type SubOrgAccessRow = SubOrgWithDataAccess & {
	rowId: string;
	dataAccessValue: DataAccess;
	dataAccessLabel: string;
};

const subOrgColumnHelper = createAppColumnHelper<SubOrgAccessRow>();

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/sub-organizations",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/sub-organizations",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/users/(org-users)/$orgUserId/$userId/sub-organizations",
	});

	const membershipEdit = useEntityActionAllowed({
		name: "UpdateOrganizationUserInfo",
		entityType: "OrganizationUser",
		entityId: params.orgUserId,
	});
	const canEditMembership = membershipEdit.allowed && !membershipEdit.isPending;

	const organizationUser = useQuery(
		organizationQueryOptions.users.detail(params.orgId, params.orgUserId),
	);
	const isSubcontractorUser = organizationUser.data?.subContractor != null;
	const canEditSubOrgs =
		canEditMembership && organizationUser.isSuccess && !isSubcontractorUser;
	const [nameQuery, setNameQuery] = useState("");
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(10);

	useEffect(() => {
		if (!isSubcontractorUser || search.mode !== "edit") return;
		navigate({
			to: "/org-admin/$orgId/users/$orgUserId/$userId/sub-organizations",
			params: {
				orgId: params.orgId,
				orgUserId: params.orgUserId,
				userId: params.userId,
			},
			search: { mode: "view", tab: "sub-organizations" },
		});
	}, [
		isSubcontractorUser,
		navigate,
		params.orgId,
		params.orgUserId,
		params.userId,
		search.mode,
	]);

	// Combined query using useQueries with combine - runs both queries in parallel
	// and merges results into a single object with unified loading/error states
	const { allSubOrgs, assignedSubOrgs, isLoading, isError } = useQueries({
		queries: [
			organizationQueryOptions.dataAccess.list(
				params.orgId,
				params.orgUserId,
				1,
				100,
			),
			organizationQueryOptions.subOrganizations.list(
				params.orgId,
				1,
				SUB_ORGANIZATION_MAX_PAGE_SIZE,
			),
		],
		combine: (results) => {
			const [dataAccessResult, subOrgsResult] = results;

			// Early return if still loading
			if (dataAccessResult.isPending || subOrgsResult.isPending) {
				return {
					allSubOrgs: [] as SubOrgWithDataAccess[],
					assignedSubOrgs: [] as SubOrgWithDataAccess[],
					isLoading: true,
					isPending: true,
					isError: false,
					error: null,
				};
			}

			// Handle errors
			if (dataAccessResult.isError || subOrgsResult.isError) {
				return {
					allSubOrgs: [] as SubOrgWithDataAccess[],
					assignedSubOrgs: [] as SubOrgWithDataAccess[],
					isLoading: false,
					isPending: false,
					isError: true,
					error: dataAccessResult.error || subOrgsResult.error,
				};
			}

			const dataAccessList = dataAccessResult.data ?? [];
			const subOrgsList = subOrgsResult.data ?? [];

			// Create a map of subOrganizationId -> DataAccessDisplay for fast lookup
			const dataAccessMap = new Map<string, DataAccessDisplay>();
			for (const da of dataAccessList) {
				if (da.subOrganizationId) {
					dataAccessMap.set(da.subOrganizationId, da);
				}
			}

			// Merge sub-orgs with their data access info
			const allSubOrgs: SubOrgWithDataAccess[] = subOrgsList.map((subOrg) => {
				const dataAccess = subOrg.id ? dataAccessMap.get(subOrg.id) : null;
				return {
					...subOrg,
					dataAccess: dataAccess ?? null,
					isAssigned: !!dataAccess,
				};
			});

			// Filter to only assigned sub-orgs
			const assignedSubOrgs = allSubOrgs.filter((subOrg) => subOrg.isAssigned);

			return {
				allSubOrgs,
				assignedSubOrgs,
				isLoading: false,
				isPending: false,
				isError: false,
				error: null,
			};
		},
	});

	// Derive form initial data from the combined query
	const initialFormData: SubOrgFormRow[] = useMemo(() => {
		return allSubOrgs.map((subOrg) => ({
			subOrganizationId: subOrg.id ?? "",
			subOrganizationName: subOrg.name ?? "Unknown",
			included: subOrg.isAssigned,
			dataAccess: (subOrg.dataAccess?.fullAccess
				? "All"
				: "Assigned") as DataAccess,
		}));
	}, [allSubOrgs]);

	const { form, resetForm } = useEditOrgUserSubOrgsForm({
		organizationId: params.orgId,
		organizationUserId: params.orgUserId,
		initialData: initialFormData,
		onSuccess: () => {
			navigate({
				to: "/org-admin/$orgId/users/$orgUserId/$userId/sub-organizations",
				params: {
					orgId: params.orgId,
					orgUserId: params.orgUserId,
					userId: params.userId,
				},
				search: { mode: "view", tab: "sub-organizations" },
			});
		},
	});

	const handleCancel = () => {
		resetForm();
		navigate({
			to: "/org-admin/$orgId/users/$orgUserId/$userId/sub-organizations",
			params: {
				orgId: params.orgId,
				orgUserId: params.orgUserId,
				userId: params.userId,
			},
			search: { mode: "view", tab: "sub-organizations" },
		});
	};

	const formSubOrgs = useStore(
		form.store,
		(state) =>
			((state as { values: { subOrganizations?: SubOrgFormRow[] } }).values
				.subOrganizations as SubOrgFormRow[]) || [],
	);

	const allDataLabel = t(
		"org-admin.sub-organizations.tabs.data-access.allData",
	);
	const assignedOnlyLabel = t(
		"org-admin.sub-organizations.tabs.data-access.assignedOnly",
	);

	const baseRows: SubOrgAccessRow[] = useMemo(() => {
		const source = canEditSubOrgs ? allSubOrgs : assignedSubOrgs;
		return source.map((subOrg) => {
			const rowId = subOrg.id ?? "";
			const formRow = formSubOrgs.find(
				(item) => item.subOrganizationId === rowId,
			);
			const dataAccessValue: DataAccess =
				formRow?.dataAccess ??
				(subOrg.dataAccess?.fullAccess ? "All" : "Assigned");
			return {
				...subOrg,
				rowId,
				dataAccessValue,
				dataAccessLabel:
					dataAccessValue === "All" ? allDataLabel : assignedOnlyLabel,
			};
		});
	}, [
		allDataLabel,
		allSubOrgs,
		assignedOnlyLabel,
		assignedSubOrgs,
		canEditSubOrgs,
		formSubOrgs,
	]);

	const query = nameQuery.trim().toLowerCase();
	const filteredRows = query
		? baseRows.filter((row) => (row.name ?? "").toLowerCase().includes(query))
		: baseRows;
	const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
	const safePageIndex = Math.min(pageIndex, pageCount - 1);
	const pageRows = filteredRows.slice(
		safePageIndex * pageSize,
		safePageIndex * pageSize + pageSize,
	);

	const selectedKeys = useMemo(
		() =>
			new Set(
				formSubOrgs
					.filter((row) => row.included)
					.map((row) => row.subOrganizationId),
			),
		[formSubOrgs],
	);
	const selectedCount = canEditSubOrgs
		? selectedKeys.size
		: assignedSubOrgs.length;

	const setIncluded = (ids: Set<string>, included: boolean) => {
		const current = form.state.values.subOrganizations;
		form.setFieldValue(
			"subOrganizations",
			current.map((row) =>
				ids.has(row.subOrganizationId) ? { ...row, included } : row,
			),
		);
	};
	const pageIncludedCount = pageRows.filter((row) =>
		selectedKeys.has(row.rowId),
	).length;
	const allPageIncluded =
		pageRows.length > 0 && pageIncludedCount === pageRows.length;

	const setDataAccess = (subOrganizationId: string, dataAccess: DataAccess) => {
		const current = form.state.values.subOrganizations;
		form.setFieldValue(
			"subOrganizations",
			current.map((row) =>
				row.subOrganizationId === subOrganizationId
					? { ...row, dataAccess }
					: row,
			),
		);
	};

	const selectionColumn = subOrgColumnHelper.display({
		id: "included",
		header: () => (
			<Checkbox
				slot={null}
				isSelected={allPageIncluded}
				isIndeterminate={pageIncludedCount > 0 && !allPageIncluded}
				aria-label={t("admin.jobTypes.drawer.access.selectAll")}
				onChange={() =>
					setIncluded(
						new Set(pageRows.map((row) => row.rowId)),
						!allPageIncluded,
					)
				}
			/>
		),
		enableSorting: false,
		enableHiding: false,
		enableResizing: false,
		size: TABLE_SELECTION_GUTTER_WIDTH_PX,
		minSize: TABLE_SELECTION_GUTTER_WIDTH_PX,
		maxSize: TABLE_SELECTION_GUTTER_WIDTH_PX,
		meta: {
			isSelectionGutter: true,
			disableColumnReorder: true,
			disableCellClamp: true,
		},
		cell: ({ row }) => (
			<Checkbox
				slot={null}
				isSelected={selectedKeys.has(row.original.rowId)}
				data-testid={`org-user-suborg-include-${row.original.rowId}`}
				aria-label={row.original.name ?? ""}
				onChange={(isSelected: boolean) =>
					setIncluded(new Set([row.original.rowId]), Boolean(isSelected))
				}
			/>
		),
	});

	const columns = subOrgColumnHelper.columns([
		...(canEditSubOrgs ? [selectionColumn] : []),
		subOrgColumnHelper.display({
			id: "subOrgIcon",
			header: "",
			enableSorting: false,
			enableHiding: false,
			size: TABLE_ICON_GUTTER_WIDTH_PX,
			minSize: TABLE_ICON_GUTTER_WIDTH_PX,
			maxSize: TABLE_ICON_GUTTER_WIDTH_PX,
			meta: { isIconGutter: true, disableColumnReorder: true },
			cell: () => <FeaturedIconCell icon={Building05} />,
		}),
		subOrgColumnHelper.accessor((row) => row.name ?? "", {
			id: "name",
			header: t("org-admin.sub-organizations.table.columns.name"),
			enableSorting: false,
			minSize: 160,
			meta: {
				headerProps: { isRowHeader: true },
			},
			cell: () => <PrimaryTextCell />,
		}),
		subOrgColumnHelper.accessor("dataAccessLabel", {
			id: "dataAccess",
			header: t("admin.users.dataAccess"),
			enableSorting: false,
			minSize: 180,
			cell: ({ row }) =>
				canEditSubOrgs ? (
					<Select
						aria-label={t("admin.users.dataAccess")}
						size="sm"
						selectedKey={row.original.dataAccessValue}
						onSelectionChange={(key) => {
							if (key !== "All" && key !== "Assigned") return;
							setDataAccess(row.original.rowId, key);
						}}
						className="w-full"
					>
						<SelectItem id="All">{allDataLabel}</SelectItem>
						<SelectItem id="Assigned">{assignedOnlyLabel}</SelectItem>
					</Select>
				) : (
					<SecondaryTextCell />
				),
		}),
	]);

	return (
		<div className="flex w-full flex-col gap-3">
			<p className="text-sm text-secondary">
				{canEditSubOrgs
					? t("org-admin.users.subOrganizations.selectAccess", {
							labels: `${t("subOrganization.entityLabel")}s`,
						})
					: t("admin.org-users.subOrganizations.description", {
							subOrganization: t("subOrganization.entityLabel"),
						})}
			</p>
			{isError ? (
				<TableSearchEmptyState title={t("common.actions.errorLoading")} />
			) : (
				<TableView
					tableKey="web.org-admin.users.sub-organizations"
					data={pageRows}
					columns={columns}
					showHeaderPadding={false}
					isLoading={isLoading}
					enableSorting={false}
					enableTableActions
					filterLayout="bar"
					filterValue={nameQuery}
					onFilterChange={(value) => {
						setNameQuery(value);
						setPageIndex(0);
					}}
					filterPlaceholder={t("tickets.detail.assignee.searchPlaceholder")}
					emptyState={
						baseRows.length === 0 ? (
							<TableListEmptyState
								title={t("admin.users.tabs.subOrganizations.dataAccess.empty")}
							/>
						) : (
							<TableSearchEmptyState />
						)
					}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex: safePageIndex }}
					onPaginationChange={(next) => {
						setPageIndex(next.pageIndex);
						setPageSize(next.pageSize);
					}}
					pageCount={pageCount}
					rowCount={filteredRows.length}
					totalRowCount={baseRows.length}
					selectedCount={selectedCount}
					getRowId={(row) => row.rowId}
					className="w-full"
				/>
			)}
			{canEditSubOrgs ? (
				<form.Subscribe
					selector={(state: unknown) => {
						const formState = state as {
							canSubmit: boolean;
							isDirty: boolean;
							isSubmitting: boolean;
						};
						return [
							formState.canSubmit,
							formState.isDirty,
							formState.isSubmitting,
						] as const;
					}}
				>
					{([canSubmit, isDirty, isSubmitting]: readonly [
						boolean,
						boolean,
						boolean,
					]) =>
						isDirty ? (
							<div className="flex justify-end gap-3 border-t border-secondary pt-4">
								<Button color="secondary" size="md" onClick={handleCancel}>
									{t("common.actions.cancelEdits")}
								</Button>
								<Button
									color="primary"
									size="md"
									data-testid="org-user-suborgs-save-btn"
									isLoading={isSubmitting}
									isDisabled={!canSubmit || !isDirty}
									onClick={() => form.handleSubmit()}
								>
									{t("common.actions.saveEdits")}
								</Button>
							</div>
						) : null
					}
				</form.Subscribe>
			) : null}
		</div>
	);
}
